import { useState, useEffect } from "react";

/**
 * Day 1 Mission card — shown until a user completes their first workout
 * AND logs their first meal, giving new users a concrete on-ramp.
 *
 * Disappears when:
 *   - Both tasks are done (brief "all set" animation, then fades out)
 *   - The user manually dismisses it
 *
 * Props:
 *   workouts   — array of workout objects (from App state)
 *   mealLog    — object keyed by date string → array of meal entries
 */
export default function Day1Mission({ workouts, mealLog }) {
  const STORAGE_KEY = "day1_dismissed";

  const hasWorkout = workouts.length > 0;
  const hasMeal = Object.values(mealLog).some((entries) => entries.length > 0);
  const allDone = hasWorkout && hasMeal;

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "1"
  );
  const [celebrating, setCelebrating] = useState(false);

  // When all tasks complete, flash the celebration state then auto-dismiss
  useEffect(() => {
    if (allDone && !dismissed) {
      setCelebrating(true);
      const t = setTimeout(() => {
        localStorage.setItem(STORAGE_KEY, "1");
        setDismissed(true);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [allDone, dismissed]);

  // Already dismissed or no longer needed
  if (dismissed) return null;

  const tasks = [
    {
      label: "Complete onboarding",
      sub: "Profile set up",
      done: true, // they're on the home screen, so this is always done
    },
    {
      label: "Log your first meal",
      sub: "Use Today's Plan below",
      done: hasMeal,
    },
    {
      label: "Start your first workout",
      sub: "Generate a workout below",
      done: hasWorkout,
    },
  ];

  const doneCount = tasks.filter((t) => t.done).length;

  return (
    <div
      style={{
        background: "var(--surface2)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: "16px 18px",
        marginBottom: 16,
        transition: "opacity 0.4s ease",
        opacity: celebrating ? 0.6 : 1,
      }}
    >
      {celebrating ? (
        /* All done — brief celebration */
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
            You&apos;re all set!
          </div>
          <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 4 }}>
            First 48 hours complete. Keep the streak alive.
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
                Day 1 Mission
              </div>
              <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
                {doneCount} of {tasks.length} complete
              </div>
            </div>
            <button
              onClick={() => {
                localStorage.setItem(STORAGE_KEY, "1");
                setDismissed(true);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text3)",
                fontSize: 18,
                lineHeight: 1,
                padding: "2px 4px",
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: 3,
              background: "var(--border)",
              borderRadius: 2,
              marginBottom: 14,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${(doneCount / tasks.length) * 100}%`,
                background: "var(--accent)",
                borderRadius: 2,
                transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
              }}
            />
          </div>

          {/* Task list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tasks.map((task) => (
              <div
                key={task.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  opacity: task.done ? 0.5 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                {/* Checkbox */}
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    border: task.done
                      ? "none"
                      : "1.5px solid var(--text3)",
                    background: task.done ? "var(--accent)" : "transparent",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                >
                  {task.done && (
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="#fff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {/* Label */}
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--text)",
                      textDecoration: task.done ? "line-through" : "none",
                    }}
                  >
                    {task.label}
                  </div>
                  {!task.done && (
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>
                      {task.sub}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
